import { createEmptyChambre } from "./clientDevisFormDefaults"
import { api } from "../../services/api"
import { toast } from "react-toastify"

const DIMS = {
  grande: "L 150–300 × l 60–150 × H 80–240 cm",
  moyen: "L 20–60 × l 20–60 × H 20–60 cm",
  petit: "L 60–150 × l 40–80 × H 40–120 cm",
}

const SIZES = [
  { key: "grande", label: "Grand" },
  { key: "moyen", label: "Moyen" },
  { key: "petit", label: "Petit" },
]

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="flex items-center mb-3">
      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center mr-3">
        <span className="text-lg">{icon}</span>
      </div>
      <div>
        <h2 className="text-lg font-bold text-white">{title}</h2>
        {subtitle ? <p className="text-gray-400 text-xs">{subtitle}</p> : null}
      </div>
    </div>
  )
}

function inputClass(hasError) {
  return `w-full px-3 py-2.5 bg-gray-800/50 border rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-1 transition-all ${
    hasError ? "border-red-500 focus:ring-red-500/20" : "border-gray-600 focus:border-amber-500 focus:ring-amber-500/20"
  }`
}

export function DevisMobileAlignedFields({
  variant,
  form,
  setForm,
  errors,
  supplementalSlot,
  belowAddressesSlot,
  onMapPickRequest,
  selectingOnMap,
  busyTimeHint,
}) {
  const updateCuisine = (size, raw) => {
    const v = String(raw).replace(/\D/g, "")
    setForm((prev) => ({
      ...prev,
      cuisine: { ...prev.cuisine, [size]: v },
    }))
  }

  const updateChambreArticle = (idx, size, raw) => {
    const v = String(raw).replace(/\D/g, "")
    setForm((prev) => {
      const ch = [...(prev.chambres || [])]
      if (!ch[idx]) return prev
      ch[idx] = {
        ...ch[idx],
        articles: { ...ch[idx].articles, [size]: v === "" ? "0" : v },
      }
      return { ...prev, chambres: ch }
    })
  }

  const updateChambreNom = (idx, nom) => {
    setForm((prev) => {
      const ch = [...(prev.chambres || [])]
      if (!ch[idx]) return prev
      ch[idx] = { ...ch[idx], nom }
      return { ...prev, chambres: ch }
    })
  }

  const addChambre = () => {
    setForm((prev) => ({
      ...prev,
      chambres: [...(prev.chambres || []), createEmptyChambre()],
    }))
  }

  const removeChambre = (idx) => {
    setForm((prev) => {
      const ch = [...(prev.chambres || [])]
      if (ch.length <= 1) return prev
      ch.splice(idx, 1)
      return { ...prev, chambres: ch }
    })
  }

  const pushPhotoUrls = async (files) => {
    const urls = []
    for (const f of files) {
      const res = await api.uploadFile(f)
      const u = res?.url || res?.imageUrl
      if (u) urls.push(u)
    }
    return urls
  }

  const onCuisineFiles = async (size, e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    if (!files.length) return
    try {
      const urls = await pushPhotoUrls(files)
      if (urls.length) {
        setForm((prev) => ({
          ...prev,
          cuisinePhotos: {
            ...prev.cuisinePhotos,
            [size]: [...(prev.cuisinePhotos?.[size] || []), ...urls],
          },
        }))
      }
    } catch {
      toast.error("Erreur lors de l'envoi des photos. Réessayez.")
    }
  }

  const removeCuisinePhoto = (size, index) => {
    setForm((prev) => ({
      ...prev,
      cuisinePhotos: {
        ...prev.cuisinePhotos,
        [size]: (prev.cuisinePhotos?.[size] || []).filter((_, i) => i !== index),
      },
    }))
  }

  const onChambreFiles = async (chIdx, size, e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    if (!files.length) return
    try {
      const urls = await pushPhotoUrls(files)
      if (urls.length) {
        setForm((prev) => {
          const ch = [...(prev.chambres || [])]
          if (!ch[chIdx]) return prev
          const photos = { ...ch[chIdx].photos, [size]: [...(ch[chIdx].photos?.[size] || []), ...urls] }
          ch[chIdx] = { ...ch[chIdx], photos }
          return { ...prev, chambres: ch }
        })
      }
    } catch {
      toast.error("Erreur lors de l'envoi des photos. Réessayez.")
    }
  }

  const removeChambrePhoto = (chIdx, size, photoIndex) => {
    setForm((prev) => {
      const ch = [...(prev.chambres || [])]
      if (!ch[chIdx]) return prev
      const list = (ch[chIdx].photos?.[size] || []).filter((_, i) => i !== photoIndex)
      ch[chIdx] = { ...ch[chIdx], photos: { ...ch[chIdx].photos, [size]: list } }
      return { ...prev, chambres: ch }
    })
  }

  const renderSizeBlock = (context, chIdx, sizeKey, label) => {
    const isKitchen = context === "cuisine"
    const countStr = isKitchen
      ? String(form.cuisine?.[sizeKey] ?? "")
      : String(form.chambres?.[chIdx]?.articles?.[sizeKey] ?? "0")
    const photos = isKitchen
      ? form.cuisinePhotos?.[sizeKey] || []
      : form.chambres?.[chIdx]?.photos?.[sizeKey] || []

    return (
      <div key={`${context}-${chIdx}-${sizeKey}`} className="p-3 rounded-lg bg-gray-800/40 border border-gray-700 space-y-2">
        <div className="flex flex-wrap items-end gap-2 justify-between">
          <div>
            <span className="text-sm font-medium text-amber-200">{label}</span>
            <p className="text-[11px] text-gray-500 mt-0.5">{DIMS[sizeKey]}</p>
          </div>
          <div className="w-24">
            <label className="block text-[10px] text-gray-400 mb-1">Qté</label>
            <input
              type="text"
              inputMode="numeric"
              value={countStr}
              onChange={(e) => {
                const t = e.target.value.replace(/\D/g, "")
                if (isKitchen) updateCuisine(sizeKey, t)
                else updateChambreArticle(chIdx, sizeKey, t === "" ? "0" : t)
              }}
              className={inputClass(false)}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="cursor-pointer text-xs px-3 py-1.5 rounded-md bg-amber-500/20 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30">
            Ajouter photo(s)
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => (isKitchen ? onCuisineFiles(sizeKey, e) : onChambreFiles(chIdx, sizeKey, e))}
            />
          </label>
          <span className="text-[11px] text-gray-500">{photos.length} photo(s)</span>
        </div>
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {photos.map((url, pi) => (
              <div key={`${url}-${pi}`} className="relative w-14 h-14 rounded border border-gray-600 overflow-hidden group">
                <img src={url} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() =>
                    isKitchen ? removeCuisinePhoto(sizeKey, pi) : removeChambrePhoto(chIdx, sizeKey, pi)
                  }
                  className="absolute inset-0 bg-black/60 text-white text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center"
                >
                  Retirer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {variant === "demenagement" && (
        <>
          <div>
            <SectionHeader icon="🍳" title="Cuisine" subtitle="Nombre d’articles par taille — une photo par article indiqué" />
            <p className="text-gray-500 text-xs mb-2">Dimensions indicatives (L × l × H)</p>
            <div className="space-y-3">{SIZES.map((s) => renderSizeBlock("cuisine", 0, s.key, s.label))}</div>
          </div>

          <div>
            <SectionHeader icon="🛏️" title="Pièces / chambres" subtitle="Ajoutez une ligne par pièce" />
            {(form.chambres || []).map((ch, idx) => (
              <div key={idx} className="mb-4 p-4 rounded-xl bg-gray-800/30 border border-gray-700 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-white">Pièce {idx + 1}</span>
                  {(form.chambres || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeChambre(idx)}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Supprimer
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Nom de la pièce</label>
                  <input
                    type="text"
                    value={ch.nom || ""}
                    onChange={(e) => updateChambreNom(idx, e.target.value)}
                    placeholder="Ex. Chambre parentale, Bureau…"
                    className={inputClass(false)}
                  />
                </div>
                <p className="text-gray-500 text-xs">Articles par taille</p>
                <div className="space-y-3">{SIZES.map((s) => renderSizeBlock("chambre", idx, s.key, s.label))}</div>
              </div>
            ))}
            <button
              type="button"
              onClick={addChambre}
              className="w-full py-2 text-sm text-amber-400 border border-dashed border-amber-500/40 rounded-lg hover:bg-amber-500/10"
            >
              + Ajouter une pièce
            </button>
          </div>
        </>
      )}

      {variant === "transport" && (
        <div>
          <SectionHeader icon="📦" title="Article à transporter" subtitle="Décrivez ce que nous devons transporter" />
          <textarea
            value={form.articleTransport || ""}
            onChange={(e) => setForm((prev) => ({ ...prev, articleTransport: e.target.value }))}
            rows={4}
            placeholder="Dimensions, poids approximatif, fragilité…"
            className={inputClass(!!errors.articleTransport)}
          />
          {errors.articleTransport && <p className="mt-1.5 text-xs text-red-400">{errors.articleTransport}</p>}
        </div>
      )}

      <div>
        <SectionHeader icon="📍" title="Adresses" subtitle="Départ et arrivée — saisie ou point sur la carte" />
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-2">Adresse de départ *</label>
            <div className="relative">
              <input
                type="text"
                value={form.depart || ""}
                onChange={(e) => setForm((prev) => ({ ...prev, depart: e.target.value }))}
                placeholder="Adresse complète de départ"
                className={`${inputClass(!!errors.depart)} ${onMapPickRequest ? "pr-11" : ""}`}
              />
              {onMapPickRequest && (
                <button
                  type="button"
                  title="Choisir sur la carte"
                  onClick={() => onMapPickRequest("depart")}
                  className={`absolute inset-y-0 right-0 px-2 flex items-center rounded-r-lg transition-colors ${
                    selectingOnMap === "depart" ? "text-green-400 animate-pulse" : "text-amber-400 hover:text-amber-300"
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}
            </div>
            {errors.depart && <p className="mt-1.5 text-xs text-red-400">{errors.depart}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-2">Adresse d’arrivée *</label>
            <div className="relative">
              <input
                type="text"
                value={form.arrivee || ""}
                onChange={(e) => setForm((prev) => ({ ...prev, arrivee: e.target.value }))}
                placeholder="Adresse complète d’arrivée"
                className={`${inputClass(!!errors.arrivee)} ${onMapPickRequest ? "pr-11" : ""}`}
              />
              {onMapPickRequest && (
                <button
                  type="button"
                  title="Choisir sur la carte"
                  onClick={() => onMapPickRequest("arrivee")}
                  className={`absolute inset-y-0 right-0 px-2 flex items-center rounded-r-lg transition-colors ${
                    selectingOnMap === "arrivee" ? "text-red-400 animate-pulse" : "text-amber-400 hover:text-amber-300"
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}
            </div>
            {errors.arrivee && <p className="mt-1.5 text-xs text-red-400">{errors.arrivee}</p>}
          </div>
        </div>
        {belowAddressesSlot ? <div className="mt-4">{belowAddressesSlot}</div> : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2">Date *</label>
          <input
            type="date"
            value={form.date || ""}
            min={typeof window !== "undefined" ? new Date().toISOString().slice(0, 10) : undefined}
            onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
            className={inputClass(!!errors.date)}
          />
          {errors.date && <p className="mt-1.5 text-xs text-red-400">{errors.date}</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2">Heure *</label>
          <input
            type="time"
            value={form.heure || "09:00"}
            onChange={(e) => setForm((prev) => ({ ...prev, heure: e.target.value }))}
            className={inputClass(!!errors.heure)}
          />
          {errors.heure && <p className="mt-1.5 text-xs text-red-400">{errors.heure}</p>}
        </div>
      </div>
      {busyTimeHint && !errors.heure ? (
        <p className="text-xs text-amber-200/95 -mt-2">{busyTimeHint}</p>
      ) : null}

      <div>
        <SectionHeader icon="🚛" title="Type de prestation" subtitle="Camion seul ou avec équipe" />
        <div className="space-y-2">
          {[
            { value: "camion_seul", label: "Camion seul (vous chargez)" },
            { value: "avec_travailleurs", label: "Avec déménageurs" },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                form.moverServiceMode === opt.value
                  ? "border-amber-500 bg-amber-500/10"
                  : "border-gray-600 bg-gray-800/40 hover:border-gray-500"
              }`}
            >
              <input
                type="radio"
                name="moverServiceMode"
                value={opt.value}
                checked={form.moverServiceMode === opt.value}
                onChange={() =>
                  setForm((prev) => ({
                    ...prev,
                    moverServiceMode: opt.value,
                    ...(opt.value === "camion_seul"
                      ? {
                          propertyType: "maison",
                          floor: "",
                          hasElevator: false,
                          ...(variant === "demenagement"
                            ? {
                                services: {
                                  emballage: false,
                                  montage: false,
                                  garde_meuble: false,
                                  cartons: false,
                                },
                              }
                            : {}),
                        }
                      : {}),
                  }))
                }
                className="text-amber-500 focus:ring-amber-500"
              />
              <span className="text-sm text-gray-200">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {variant === "demenagement" && form.moverServiceMode === "avec_travailleurs" && supplementalSlot ? (
        <div className="space-y-2">{supplementalSlot}</div>
      ) : null}

      {form.moverServiceMode === "avec_travailleurs" && (
        <div className="space-y-4">
          <div>
            <SectionHeader icon="🏠" title="Type de logement" subtitle="Maison ou appartement" />
            <div className="flex flex-wrap gap-3">
              {["maison", "appartement"].map((pt) => (
                <label
                  key={pt}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer ${
                    form.propertyType === pt ? "border-amber-500 bg-amber-500/10" : "border-gray-600 bg-gray-800/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="propertyType"
                    value={pt}
                    checked={form.propertyType === pt}
                    onChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        propertyType: pt,
                        ...(pt === "maison" ? { floor: "", hasElevator: false } : {}),
                      }))
                    }
                  />
                  <span className="text-sm capitalize text-gray-200">{pt}</span>
                </label>
              ))}
            </div>
          </div>
          {form.propertyType === "appartement" && (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-2">Étage *</label>
                <input
                  type="text"
                  value={form.floor || ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, floor: e.target.value }))}
                  placeholder="Ex. 3e étage"
                  className={inputClass(!!errors.floor)}
                />
                {errors.floor && <p className="mt-1.5 text-xs text-red-400">{errors.floor}</p>}
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-300 mb-2">Ascenseur</p>
                <div className="flex gap-4">
                  {[
                    { v: true, l: "Oui" },
                    { v: false, l: "Non" },
                  ].map((o) => (
                    <label key={o.l} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="radio"
                        name="hasElevator"
                        checked={form.hasElevator === o.v}
                        onChange={() => setForm((prev) => ({ ...prev, hasElevator: o.v }))}
                      />
                      {o.l}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {variant === "demenagement" && (
        <div>
          <SectionHeader icon="📝" title="Notes complémentaires" subtitle="Optionnel" />
          <textarea
            value={form.notes || ""}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            rows={3}
            placeholder="Accès, contraintes horaires, objets fragiles…"
            className={inputClass(false)}
          />
        </div>
      )}
    </div>
  )
}
